import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { shareTender, unshareTender } from '../../services/api';
import { getPartner } from '../../services/partnerService';
import { PERMISSIONS } from '../../shared/constants/permissions';
import type { User } from '../../types/auth';
import type { Tender } from '../../types/tender';
import { getUserAvatarColor } from '../../utils/avatarUtils';
import { ManagePermissionsDialog } from '../Dialogs/ManagePermissionsDialog';
import { TenderPermissionsForm } from './TenderPermissionsForm';

const EMPTY_OBJECT: Record<string, never> = {};

interface PartnerDelegationCardProps {
  tender: Tender;
  users: User[];
  currentUser?: User | null;
  onUpdate?: (field: keyof Tender, value: Tender[keyof Tender]) => Promise<void>;
}

const EMPTY_ARRAY: never[] = [];

const PartnerDelegationCard: React.FC<PartnerDelegationCardProps> = ({
  tender,
  users,
  currentUser,
  onUpdate,
}) => {
  const theme = useTheme();

  const [partnerName, setPartnerName] = useState<string>(tender.clientName || 'External Company');

  useEffect(() => {
    if (!tender.clientName && tender.clientId && tender.clientId !== currentUser?.clientId) {
      const fetchPartnerName = async () => {
        try {
          const partnerData = await getPartner(tender.clientId || '');
          if (partnerData?.companyName) {
            setPartnerName(String(partnerData.companyName));
          } else {
            setPartnerName('External Company');
          }
        } catch (_e) {
          setPartnerName('External Company');
        }
      };
      fetchPartnerName();
    } else if (tender.clientName) {
      setPartnerName(tender.clientName);
    } else {
      setPartnerName('External Company');
    }
  }, [tender.clientId, tender.clientName, currentUser?.clientId]);

  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [selectedShareUser, setSelectedShareUser] = useState<string>('');
  const [isSharing, setIsSharing] = useState(false);
  const [sharePermissions, setSharePermissions] = useState<string[]>([]);

  const [permissionDialogUser, setPermissionDialogUser] = useState<{
    uid: string;
    name: string;
  } | null>(null);

  const handleShareClick = () => {
    setSelectedShareUser('');
    setSharePermissions([PERMISSIONS.TENDER_EDIT]);
    setIsShareDialogOpen(true);
  };

  const handleConfirmShare = async () => {
    if (!selectedShareUser) {
      return;
    }
    const targetUser = users.find((u) => u.uid === selectedShareUser);
    if (!targetUser?.publicKey) {
      return;
    }

    setIsSharing(true);
    try {
      await shareTender(tender.id || '', targetUser.uid, targetUser.publicKey);
      if (sharePermissions.length > 0 && onUpdate) {
        const currentMap = tender.memberPermissions || EMPTY_OBJECT;
        const updatedMap = { ...currentMap, [targetUser.uid]: sharePermissions };
        await onUpdate('memberPermissions', updatedMap);
      }
    } catch (_e) {
      // Failed to share tender
    } finally {
      setIsSharing(false);
      setIsShareDialogOpen(false);
    }
  };

  const handleUnshare = async (userId: string) => {
    if (!tender.id) {
      return;
    }
    try {
      await unshareTender(tender.id, userId);
    } catch (_e) {
      // Failed to unshare
    }
  };

  const handleOpenPermissions = (uid: string, name: string) => {
    setPermissionDialogUser({ uid, name });
  };

  const handleSavePermissions = async (userId: string, permissions: string[]) => {
    if (onUpdate) {
      const currentMap = tender.memberPermissions || EMPTY_OBJECT;
      const updatedMap = { ...currentMap, [userId]: permissions };
      await onUpdate('memberPermissions', updatedMap);
    }
  };

  // We are on the partner side, so the users array is the partner's colleagues.
  // We want to list any colleague who has been shared this tender.
  const sharedUsers = users.filter((u) => tender.sharedWith?.includes(u.uid));

  // If the current user has access to this tender, they can delegate.
  // In the partner view, they only see it if it's shared with them.
  const canShare = true;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ mb: 0.5 }}>
            <Chip
              label={`PARTNER: ${partnerName}`}
              sx={{
                backgroundColor: '#C19A6B',
                color: '#000',
                fontWeight: 'bold',
                fontSize: '0.75rem',
              }}
            />
          </Box>
          <Typography variant="h6" component="h2" fontWeight="bold">
            Partner Delegation
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Share this {partnerName !== 'External Company' ? `${partnerName} ` : ''}opportunity with
            your colleagues
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canShare && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={handleShareClick}
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Share
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Shared With Section */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Colleagues With Access
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {sharedUsers.length > 0 ? (
              sharedUsers.map((user) => {
                const avatarStyle = getUserAvatarColor(user.displayName || 'U');
                const isMe = user.uid === currentUser?.uid;
                return (
                  <Box
                    key={user.uid}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      pr: canShare && !isMe ? 0.5 : 0,
                      pl: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Tooltip title={user.email || ''}>
                      <Chip
                        avatar={
                          <Avatar
                            style={{
                              backgroundColor: avatarStyle.bgcolor,
                              color: avatarStyle.color,
                            }}
                          >
                            {(user.displayName || 'U').charAt(0).toUpperCase()}
                          </Avatar>
                        }
                        label={isMe ? `${user.displayName} (You)` : user.displayName}
                        size="small"
                        variant="outlined"
                        sx={{ border: 'none' }}
                        {...(canShare && !isMe ? { onDelete: () => handleUnshare(user.uid) } : {})}
                      />
                    </Tooltip>
                    {canShare && !isMe && (
                      <Tooltip title="Manage Permissions">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleOpenPermissions(user.uid, user.displayName || 'User')
                          }
                          sx={{ p: 0.5 }}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Not shared with any colleagues.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Share Dialog */}
      <Dialog
        open={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share with Colleague</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a colleague to grant them access to this tender workspace.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="share-user-select-label">Select User</InputLabel>
            <Select
              labelId="share-user-select-label"
              value={selectedShareUser}
              label="Select User"
              onChange={(e) => setSelectedShareUser(e.target.value)}
            >
              {users
                .filter((u) => !tender.sharedWith?.includes(u.uid) && u.publicKey)
                .map((user) => (
                  <MenuItem key={user.uid} value={user.uid}>
                    {user.displayName} ({user.email})
                  </MenuItem>
                ))}
            </Select>
            {users.some((u) => !u.publicKey) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Note: Some users may not appear if they haven't set up encryption keys yet.
              </Typography>
            )}
          </FormControl>

          {selectedShareUser && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}
              >
                PERMISSIONS FOR THIS TENDER
              </Typography>
              <TenderPermissionsForm
                permissions={sharePermissions}
                onChange={setSharePermissions}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsShareDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmShare}
            variant="contained"
            color="primary"
            disabled={!selectedShareUser || isSharing}
          >
            {isSharing ? 'Sharing...' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Dialog */}
      {permissionDialogUser && (
        <ManagePermissionsDialog
          open={!!permissionDialogUser}
          onClose={() => setPermissionDialogUser(null)}
          userId={permissionDialogUser.uid}
          userName={permissionDialogUser.name}
          currentPermissions={tender.memberPermissions?.[permissionDialogUser.uid] || EMPTY_ARRAY}
          onSave={handleSavePermissions}
        />
      )}
    </Paper>
  );
};

export default PartnerDelegationCard;
